import React, { useState } from "react";

import { View, StyleSheet } from "react-native";
import { ActionsProps, IMessage } from "react-native-gifted-chat";
import { Img, Icon, Text, Button, EditModal, Input } from "components";
import { colors, strings } from "assets/constants";
import { useFlag } from "hooks";
import css from "styles";

const SelectableLocalButton = (props: {
  color: string;
  title: string;
  icon: IconT;
  onPress: () => void;
}) => {
  const { title, icon, color, onPress } = props;

  return (
    <Button
      onPress={onPress}
      bgColor={colors.white}
      style={{
        width: 100,
        height: 100,
        borderRadius: 25,
        borderWidth: 2,
        borderColor: color + "22",
        elevation: 5,
      }}
      renderContent={() => (
        <View style={{ ...css.container, ...css.centralElements }}>
          <Icon type="entypo" color={color} size={50} {...icon} />
          <Text weight="bold" size={"lg"} color={color}>
            {title}
          </Text>
        </View>
      )}
    />
  );
};

import * as ImagePicker from "expo-image-picker";
import * as DocumentPicker from "expo-document-picker";
import { dataUrlToFile } from "utils";

interface ActionsT extends ActionsProps {
  onSend: (msg: IMessage[]) => void;
}

interface SelectedFileT {
  uri: string;
  type: string;
  name: string;
  size: number;
  ratio?: number;
  fileCopyUri?: string;
}

export default function ChatActions(props: ActionsT) {
  const { onSend } = props;

  const [desc, setDesc] = useState("");
  const [selectedFile, setSelectedFile] = useState<SelectedFileT | null>(null);

  const { flg: attach, on: showAttach, off: hideAttach } = useFlag();

  const options: ImagePicker.ImagePickerOptions = {
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
  };

  const imagePickerCallback = (res: ImagePicker.ImagePickerResult) => {
    if (res?.canceled) {
      return;
    }
    const pic = res?.assets?.[0];

    const type = String(pic.uri.split(";base64,")[0].split(":"));
    const name = String(Date.now());
    const uri = pic?.uri;
    const ratio = pic?.width && pic?.height ? pic?.width / pic?.height : 1;

    pic &&
      setSelectedFile({
        name,
        uri,
        type,
        ratio,
        size: 0,
      });
  };

  const _onPickFromCamera = () => {
    hideAttach();
    ImagePicker.launchCameraAsync(options).then(imagePickerCallback);
  };

  const _onPickFromGallery = () => {
    hideAttach();
    ImagePicker.launchImageLibraryAsync(options).then(imagePickerCallback);
  };

  const _onPickFromFileManager = () => {
    hideAttach();
    DocumentPicker.getDocumentAsync({ copyToCacheDirectory: true }).then(
      (res) => {
        if (res.type === "success") {
          setSelectedFile({
            name: res?.name || "",
            uri: res?.uri,
            fileCopyUri: res.uri || "",
            type: res.mimeType || "",
            size: res?.size || 0,
          });
        }
      }
    );
  };

  const _onSend = () => {
    const { name, uri, type, size, ratio, fileCopyUri } =
      selectedFile as SelectedFileT;

    const msg: IMessage = {
      _id: "",
      text: desc,
      ...(fileCopyUri
        ? { file: { uri: fileCopyUri || uri, type, name, size } }
        : {}),
      createdAt: new Date(),
      user: { _id: 1 },
      pending: true,
      sent: false,
      received: false,
      ...(ratio ? { image: uri } : {}),
    };

    onSend([msg]);
    setSelectedFile(null);
  };

  return (
    <>
      <View style={styles.container}>
        <Button
          onPress={showAttach}
          bgColor={colors.white}
          width={40}
          height={50}
          icon={{
            name: "paperclip",
            size: 20,
            color: colors.textColorDark,
          }}
        />
      </View>

      <EditModal
        hideToast
        closeByTouchOut
        visible={attach}
        onCancel={hideAttach}
        renderContent={() => {
          return (
            <View
              style={{
                ...css.row,
                ...css.justifySpaceEvenly,
                paddingVertical: 30,
              }}
            >
              <SelectableLocalButton
                onPress={_onPickFromCamera}
                title={strings.camera}
                color={colors.darkSuccess}
                icon={{ name: "camera" }}
              />
              {/* <SelectableLocalButton
                onPress={_onPickFromFileManager}
                title={strings.files}
                color={colors.theme}
                icon={{ name: "folder" }}
              /> */}
              <SelectableLocalButton
                onPress={_onPickFromGallery}
                title={strings.gallery}
                color={colors.blue}
                icon={{ name: "images" }}
              />
            </View>
          );
        }}
      />

      <EditModal
        hideToast
        visible={selectedFile !== null}
        onCancel={() => setSelectedFile(null)}
        renderContent={() => {
          return (
            <View style={{ paddingVertical: 10 }}>
              {selectedFile?.ratio ? (
                <>
                  <View>
                    <Img
                      resizeMode={"contain"}
                      source={{ uri: selectedFile.uri }}
                      style={{
                        width: "90%",
                        aspectRatio: 1.5,
                        ...css.selfCenter,
                        borderRadius: 15,
                        marginBottom: 20,
                        backgroundColor: colors.white,
                      }}
                    />
                    <Input
                      style={styles.desc}
                      placeholder="توضیحات را وارد کنید"
                      onChangeText={setDesc}
                    />
                    {/* <Text weight="bold" size={"lg"} align="center">
                      {strings.sure_to_send_image}
                    </Text> */}
                  </View>
                </>
              ) : (
                <>
                  <Icon
                    type="entypo"
                    name="text-document-inverted"
                    size={50}
                    style={{ ...css.selfCenter }}
                  />
                  <Text
                    style={{
                      marginTop: 10,
                      lineHeight: 28,
                    }}
                    align="center"
                    weight="bold"
                    size={"xxl"}
                  >
                    {strings.sure_to_send_file(selectedFile?.name || "")}
                  </Text>
                </>
              )}

              <View
                style={{
                  ...css.row,
                  ...css.justifySpaceBetween,
                  marginTop: 40,
                  width: "90%",
                  alignSelf: "center",
                }}
              >
                <Button
                  width={"46%"}
                  title={{ value: strings.cancel }}
                  outline
                  onPress={() => {
                    setDesc("");
                    setSelectedFile(null);
                  }}
                />
                <Button
                  onPress={_onSend}
                  width={"46%"}
                  title={{ value: strings.confirm }}
                />
              </View>
            </View>
          );
        }}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
  },

  attachModalContainer: {
    flexDirection: "row",
    justifyContent: "space-evenly",
    paddingVertical: 30,
  },

  recorder: {
    position: "absolute",
    width: "100%",
    height: 50,
    zIndex: 1000,
  },

  desc: { width: "90%", alignSelf: "center", paddingHorizontal: 7 },
});
